// Lightweight, GDPR-aware analytics helper.
//
// Strategy:
//   • Always dispatches a `window.CustomEvent("cta_click", { detail })` so any
//     provider can listen later without us coupling to one SDK now.
//   • If the user has accepted analytics cookies, also pushes to
//     `window.dataLayer` (compatible with GA4 / Google Tag Manager) and calls
//     `window.gtag` if present.
//
// Wire a real provider (GA4, Plausible, PostHog) by listening to either
// `window.dataLayer` or the `cta_click` DOM event. No analytics calls fire
// without consent except the local DOM event (which never leaves the page).

import { getCookiePreferences } from "@/components/CookieConsent";

type AnalyticsParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

function hasAnalyticsConsent(): boolean {
  try {
    const prefs = getCookiePreferences();
    return !!prefs?.analytics;
  } catch {
    return false;
  }
}

/**
 * Track a CTA click.
 *
 * @param ctaId  Stable identifier, e.g. "send_inquiry" or "request_guidance".
 * @param params Additional context (location, label, etc.).
 */
export function trackCtaClick(ctaId: string, params: AnalyticsParams = {}) {
  if (typeof window === "undefined") return;

  const detail = {
    cta_id: ctaId,
    timestamp: Date.now(),
    page_path: window.location.pathname,
    ...params,
  };

  // Always-on local event for any future listener (no PII leaves the page).
  try {
    window.dispatchEvent(new CustomEvent("cta_click", { detail }));
  } catch {
    /* no-op */
  }

  if (!hasAnalyticsConsent()) return;

  // GA4 / GTM compatible push.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "cta_click", ...detail });

  // Direct gtag call if loaded.
  if (typeof window.gtag === "function") {
    window.gtag("event", "cta_click", detail);
  }
}
